import { useState } from "react";
import { Link } from "@/components/shared/LocalizedLink";
import { T, useT } from "@/lib/translation";
import { Button } from "@/components/ui/button";
import { initPostHog } from "@/lib/posthog";

const STORAGE_KEY = "cookie_consent";

// Trans' own key typing resolves to `never` with this project's non-strict TS
// config, so keys are checked here against the English files and cast once.

export default function CookieBanner() {
  const t = useT();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(STORAGE_KEY) !== null,
  );

  if (dismissed) return null;

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    initPostHog();
    setDismissed(true);
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    setDismissed(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay — matches DialogOverlay */}
      <div className="absolute inset-0 bg-black/80" />

      {/* Modal — matches DialogContent */}
      <div className="relative w-full max-w-md border bg-background p-6 shadow-lg sm:rounded-lg">
        {/* Header — matches DialogHeader + DialogTitle */}
        <div className="flex flex-col space-y-1.5 mb-4">
          <h2 className="text-lg font-semibold leading-none tracking-tight">
            {t("onboarding:cookie.title")}
          </h2>
        </div>

        {/* Body — matches DialogDescription */}
        <p className="text-sm text-muted-foreground mb-1">
          <T
            k="onboarding:cookie.intro"
            components={{
              essential: <span className="font-medium text-foreground" />,
              analytics: <span className="font-medium text-foreground" />,
            }}
          />
        </p>
        <p className="text-sm text-muted-foreground mb-1">
          {t("onboarding:cookie.declining")}
        </p>
        <p className="text-sm text-muted-foreground mb-1">
          {t("onboarding:cookie.promise")}
        </p>
        <Link
          to="/privacy"
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors mb-6 block"
        >
          {t("onboarding:cookie.privacyPolicy")}
        </Link>

        {/* Accept and Decline are given equal visual weight on purpose. A
            prominent Accept next to a de-emphasised Decline is a deceptive
            design pattern under EDPB guidance, and makes the consent invalid. */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="w-full" onClick={handleAccept}>
            {t("onboarding:cookie.accept")}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleDecline}>
            {t("onboarding:cookie.decline")}
          </Button>
        </div>
      </div>
    </div>
  );
}
